import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { SyncStatusBadge, type SyncStatus } from '../components/SyncStatusBadge.js';
import { AdminQuery } from './AdminQuery.js';

/* ── Types ── */

interface TableInfo {
  name: string;
  row_count: number;
  group: string;
  data_sources: string[];
  last_sync_status: string | null;
  last_sync_at: string | null;
}

interface TablesResponse {
  tables: TableInfo[];
  groups: Record<string, string[]>;
}

interface ColumnInfo {
  cid: number;
  name: string;
  type: string;
  notnull: number;
  dflt_value: string | null;
  pk: number;
}

interface RowsResponse {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  limit: number;
  offset: number;
}

interface SyncJob {
  id: string;
  status: string;
  mode: string;
  started_at: string;
  completed_at: string | null;
  rows_added: number | null;
  total_chunks: number;
  completed_chunks: number;
  failed_chunks: number;
  duration_ms: number | null;
  accounts_json: string | null;
  error_message: string | null;
  jobs_enqueued?: number;
  row_count_before?: number;
}

interface SyncStatusResponse {
  linked: boolean;
  data_sources?: string[];
  lastJob: SyncJob | null;
  runningJob: SyncJob | null;
  recentJobs: SyncJob[];
}

interface ScheduleInfo {
  override: string;
  defaultInfo: {
    schedule: string;
    period: string;
    time: string;
  } | null;
}

interface SyncAccount {
  id: string;
  name: string;
  region: string;
  marketplaces: Array<{
    id: string;
    name: string;
    nickname: string;
    country_code: string;
    currency: string;
    domain: string;
  }>;
}

/* ── Main Export ── */

export function AdminTables() {
  const { name } = useParams<{ name: string }>();
  return name ? <TableDetail name={name} /> : <TableList />;
}

/* ── Table List ── */

interface StartDateRow {
  account_id: string;
  marketplace_id: string;
  earliest_order: string | null;
  marketplace_name: string;
  account_name: string;
}

function TableList() {
  const [data, setData] = useState<TablesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [startDates, setStartDates] = useState<StartDateRow[]>([]);
  const [showStartDates, setShowStartDates] = useState(false);
  const [showSqlConsole, setShowSqlConsole] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => Promise.all([
      api.get<TablesResponse>('/admin/tables').then(setData),
      api.get<StartDateRow[]>('/admin/marketplace-start-dates').then(setStartDates).catch(() => {}),
    ]).finally(() => setLoading(false));
    load();
    // Auto-refresh every 30s to pick up sync status changes
    const interval = setInterval(() => { api.get<TablesResponse>('/admin/tables').then(setData).catch(() => {}); }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const res = await api.post<{ ok: boolean; dates: StartDateRow[] }>('/admin/marketplace-start-dates/detect', {});
      if (res.dates) setStartDates(res.dates);
    } catch (err) {
      alert(`Detection failed: ${(err as Error).message}`);
    } finally {
      setDetecting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return <p className="text-gray-500">Failed to load tables.</p>;

  const grouped: Record<string, TableInfo[]> = {};
  for (const table of data.tables) {
    (grouped[table.group] ??= []).push(table);
  }

  const groupOrder = Object.keys(data.groups);

  const toggleGroup = (group: string) => {
    setCollapsed((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Database Tables</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSqlConsole(v => !v)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              showSqlConsole
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <SqlIcon />
            SQL Console
          </button>
          <button
            onClick={() => setShowStartDates(v => !v)}
            className="text-sm text-gray-500 hover:text-blue-600"
          >
            {showStartDates ? 'Hide' : 'Show'} Start Dates
          </button>
        </div>
      </div>

      {/* SQL Console */}
      {showSqlConsole && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/30 p-4 space-y-4">
          <AdminQuery />
          <div className="rounded-lg border border-blue-100 bg-white p-4 text-xs text-gray-600 space-y-2">
            <p className="font-semibold text-gray-700">How to use</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Type any SQL query in the editor and press <kbd className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5 font-mono text-[10px]">Ctrl+Enter</kbd> to run it.</li>
              <li>Click a table name above the editor to insert it at the cursor position.</li>
              <li>Enable <strong>Allow writes</strong> to run INSERT, UPDATE, or DELETE statements.</li>
              <li>Query history is saved in your browser — click <strong>Show History</strong> to re-run a past query.</li>
            </ul>
            <p className="font-semibold text-gray-700 pt-1">Example: check today's failed syncs</p>
            <code className="block rounded bg-gray-50 border border-gray-200 p-2 font-mono text-[11px] text-gray-800 whitespace-pre-wrap">
              {"SELECT e.name, sh.status, sh.error_message, sh.started_at\nFROM sync_history sh\nJOIN exports e ON sh.export_id = e.id\nWHERE sh.status = 'failed'\n  AND sh.started_at >= date('now')\nORDER BY sh.started_at DESC"}
            </code>
          </div>
        </div>
      )}

      {showStartDates && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Marketplace Start Dates</h3>
            <button
              onClick={handleDetect}
              disabled={detecting}
              className="flex items-center gap-1.5 rounded bg-gray-600 px-3 py-1 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50"
            >
              {detecting && <SpinnerIcon />}
              {detecting ? 'Detecting...' : 'Re-detect All'}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Used by full-history sync to skip date ranges before a marketplace had sales. Auto-detected on first full sync.
          </p>
          {startDates.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No start dates detected yet. Click "Re-detect All" or run a full sync.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                  <th className="py-1.5 font-medium">Account</th>
                  <th className="py-1.5 font-medium">Marketplace</th>
                  <th className="py-1.5 font-medium">First Order</th>
                  <th className="py-1.5 font-medium">Marketplace ID</th>
                </tr>
              </thead>
              <tbody>
                {startDates.map(sd => (
                  <tr key={`${sd.account_id}-${sd.marketplace_id}`} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-700">{sd.account_name}</td>
                    <td className="py-1.5 text-gray-700">{sd.marketplace_name ?? sd.marketplace_id}</td>
                    <td className="py-1.5 font-mono text-gray-900">{sd.earliest_order ?? '—'}</td>
                    <td className="py-1.5 text-xs font-mono text-gray-400">{sd.marketplace_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {groupOrder.map((group) => {
        const tables = grouped[group];
        if (!tables?.length) return null;
        const totalRows = tables.reduce((s, t) => s + t.row_count, 0);
        const isCollapsed = collapsed[group];
        return (
          <div key={group} className="rounded-lg border border-gray-200 bg-white">
            <button
              onClick={() => toggleGroup(group)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                <span className="font-semibold text-gray-900">{group}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {tables.length} tables
                </span>
              </div>
              <span className="text-sm text-gray-500">{totalRows.toLocaleString()} rows</span>
            </button>
            {!isCollapsed && (
              <div className="border-t border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs text-gray-500">
                      <th className="px-4 py-2 font-medium">Table</th>
                      <th className="px-4 py-2 font-medium text-right">Rows</th>
                      <th className="px-4 py-2 font-medium">Sync Status</th>
                      <th className="px-4 py-2 font-medium">Last Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tables.map((t) => (
                      <tr
                        key={t.name}
                        onClick={() => navigate(`/admin/tables/${t.name}`)}
                        className="cursor-pointer border-b border-gray-50 hover:bg-blue-50 transition-colors"
                      >
                        <td className="px-4 py-2 font-mono text-sm text-blue-600">{t.name}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{t.row_count.toLocaleString()}</td>
                        <td className="px-4 py-2">
                          {t.last_sync_status ? (
                            <SyncStatusBadge status={t.last_sync_status as SyncStatus} />
                          ) : t.data_sources.length > 0 ? (
                            <span className="text-xs text-gray-400">never synced</span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {t.last_sync_at ? formatRelative(t.last_sync_at) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Table Detail ── */

function TableDetail({ name }: { name: string }) {
  const [rows, setRows] = useState<RowsResponse | null>(null);
  const [schema, setSchema] = useState<ColumnInfo[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'data' | 'schema'>('data');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editCell, setEditCell] = useState<{ rowid: number | string; col: string; value: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncMode, setSyncMode] = useState<'default' | 'range' | 'full'>('default');
  const [syncStart, setSyncStart] = useState('');
  const [syncEnd, setSyncEnd] = useState('');
  const [syncResult, setSyncResult] = useState<{ ok?: boolean; error?: string; message?: string } | null>(null);
  const [syncAccounts, setSyncAccounts] = useState<SyncAccount[]>([]);
  const [syncProgress, setSyncProgress] = useState<{
    rowsAdded: number;
    rowCountNow: number;
    elapsedFormatted: string;
    total_chunks: number;
    completed_chunks: number;
  } | null>(null);
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [selected, setSelected] = useState<Set<number | string>>(new Set());
  const [schedule, setSchedule] = useState('');
  const [scheduleInfo, setScheduleInfo] = useState<ScheduleInfo | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const PAGE_SIZE = 50;

  const fetchRows = useCallback(async () => {
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (sort) {
      params.set('sort', sort.col);
      params.set('dir', sort.dir);
    }
    if (search) params.set('search', search);

    const data = await api.get<RowsResponse>(`/admin/tables/${name}/rows?${params}`);
    setRows(data);
  }, [name, page, sort, search]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchRows(),
      api.get<{ columns: ColumnInfo[] }>(`/admin/tables/${name}/schema`).then((r) => setSchema(r.columns)),
      api.get<SyncStatusResponse>(`/admin/tables/${name}/sync-status`).then((status) => {
        setSyncStatus(status);
        // Auto-resume polling if there's a running job
        if (status.runningJob) {
          setSyncing(true);
          startJobPolling(status.runningJob.id);
        }
      }),
      loadSchedule(),
    ]).finally(() => setLoading(false));
  }, [fetchRows, name]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSchedule() {
    try {
      const info = await api.get<ScheduleInfo>(`/admin/tables/${name}/schedule`);
      setScheduleInfo(info);
      setSchedule(info.override);
    } catch { /* table may not have a schedule */ }
  }

  const handleSort = (col: string) => {
    setSort((prev) =>
      prev?.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
    setPage(0);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleSaveCell = async () => {
    if (!editCell) return;
    setSaving(true);
    try {
      await api.put(`/admin/tables/${name}/rows/${editCell.rowid}`, { [editCell.col]: editCell.value });
      setEditCell(null);
      await fetchRows();
    } catch (err) {
      alert(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rowid: number | string) => {
    if (!confirm(`Delete row ${rowid} from ${name}?`)) return;
    try {
      await api.delete(`/admin/tables/${name}/rows/${rowid}`);
      await fetchRows();
    } catch (err) {
      alert(`Delete failed: ${(err as Error).message}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} rows from ${name}?`)) return;
    await api.post(`/admin/tables/${name}/rows/bulk-delete`, { rowids: [...selected] });
    setSelected(new Set());
    await fetchRows();
  };

  const openSyncDialog = () => {
    setSyncMode('default');
    setSyncStart('');
    setSyncEnd('');
    setSyncResult(null);
    setSyncDialog(true);
    api.get<SyncAccount[]>('/admin/sync-accounts').then(setSyncAccounts).catch(() => {});
  };

  const startJobPolling = (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const poll = await api.get<{
          rowsAdded: number;
          rowCountNow: number;
          elapsedFormatted: string;
          total_chunks: number;
          completed_chunks: number;
          failed_chunks: number;
          status: string;
          error_message: string | null;
        }>(`/admin/sync-jobs/${encodeURIComponent(jobId)}`);
        setSyncProgress({
          rowsAdded: poll.rowsAdded,
          rowCountNow: poll.rowCountNow,
          elapsedFormatted: poll.elapsedFormatted,
          total_chunks: poll.total_chunks,
          completed_chunks: poll.completed_chunks,
        });

        // Server-side completion detection (all chunks processed)
        if (poll.status === 'completed' || poll.status === 'failed') {
          clearInterval(pollInterval);
          setSyncing(false);
          if (poll.status === 'completed') {
            setSyncResult({
              ok: true,
              message: `Sync complete — +${poll.rowsAdded} rows (${poll.elapsedFormatted})`,
            });
          } else {
            setSyncResult({
              error: `Sync failed — ${poll.failed_chunks}/${poll.total_chunks} chunks failed${poll.error_message ? ': ' + poll.error_message.split('\n')[0] : ''}`,
            });
          }
          await fetchRows();
          const status = await api.get<SyncStatusResponse>(`/admin/tables/${name}/sync-status`);
          setSyncStatus(status);
        }
      } catch { /* ignore poll errors */ }
    }, 5000);
    // Stop polling after 30 minutes max
    setTimeout(() => {
      clearInterval(pollInterval);
      setSyncing(false);
    }, 1800000);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncProgress(null);
    try {
      const body: Record<string, string> = { mode: syncMode };
      if (syncMode === 'range') {
        if (!syncStart) { setSyncResult({ error: 'Start date is required' }); setSyncing(false); return; }
        body.startDate = syncStart;
        body.endDate = syncEnd || new Date().toISOString().slice(0, 10);
      }
      const res = await api.post<{
        ok: boolean;
        jobId?: string;
        message?: string;
        jobs_enqueued?: number;
        total_chunks?: number;
        error?: string;
      }>(`/admin/tables/${name}/sync`, body);
      if (res.error) {
        setSyncResult({ error: res.error });
      } else {
        setSyncResult({ ok: true, message: res.message ?? 'Sync jobs enqueued' });
        if (res.jobId) {
          startJobPolling(res.jobId);
          return; // Keep syncing=true until poll detects completion
        }
      }
    } catch (err) {
      setSyncResult({ error: (err as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  const handleComputeRollup = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncProgress(null);
    try {
      const res = await api.post<{ ok: boolean; jobId?: string; message?: string; error?: string }>(
        `/admin/tables/monthly_rollups/compute`, {}
      );
      if (res.error) {
        setSyncResult({ error: res.error });
        setSyncing(false);
      } else {
        setSyncResult({ ok: true, message: res.message ?? 'Computing rollups...' });
        if (res.jobId) {
          startJobPolling(res.jobId);
        }
      }
    } catch (err) {
      setSyncResult({ error: (err as Error).message });
      setSyncing(false);
    }
  };

  const handleScheduleChange = async (newSchedule: string) => {
    setScheduleLoading(true);
    try {
      await api.put(`/admin/tables/${name}/schedule`, { schedule: newSchedule });
      setSchedule(newSchedule);
    } catch (err) {
      alert(`Failed to update schedule: ${(err as Error).message}`);
    } finally {
      setScheduleLoading(false);
    }
  };

  const toggleSelect = (rowid: number | string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(rowid) ? next.delete(rowid) : next.add(rowid);
      return next;
    });
  };

  const getRowId = (r: Record<string, unknown>) => (r.rowid ?? r.id ?? r.ID) as number | string;

  const toggleSelectAll = () => {
    if (!rows) return;
    const allRowids = rows.rows.map(getRowId);
    const allSelected = allRowids.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(allRowids));
  };

  if (loading) return <LoadingSpinner />;

  const lastJob = syncStatus?.lastJob;
  const totalPages = rows ? Math.ceil(rows.total / PAGE_SIZE) : 0;

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '';
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb + Header */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/admin" className="hover:text-blue-600">Database Tables</Link>
        <span>/</span>
        <span className="font-medium text-gray-900">{name}</span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <h2 className="text-xl font-semibold font-mono">{name}</h2>
        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
          {rows?.total.toLocaleString() ?? '?'} rows
        </span>
        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
          {schema.length} columns
        </span>

        <div className="ml-auto flex items-center gap-2">
          {syncStatus?.linked && (
            <>
              <select
                value={schedule}
                onChange={(e) => handleScheduleChange(e.target.value)}
                disabled={scheduleLoading}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="">Default cron</option>
                <option value="every_6h">Every 6h</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual</option>
              </select>
              <button
                onClick={openSyncDialog}
                disabled={syncing}
                className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {syncing && <SpinnerIcon />}
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </>
          )}
          {name === 'monthly_rollups' && !syncStatus?.linked && (
            <button
              onClick={handleComputeRollup}
              disabled={syncing}
              className="flex items-center gap-1.5 rounded bg-purple-600 px-3 py-1 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {syncing && <SpinnerIcon />}
              {syncing ? 'Computing...' : 'Compute Rollups'}
            </button>
          )}
        </div>
      </div>

      {/* Schedule info */}
      {syncStatus?.linked && scheduleInfo?.defaultInfo && (
        <div className="flex items-center gap-4 text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-2 border border-gray-100">
          <span className="font-medium text-gray-600">Cron:</span>
          <span>
            Default: <span className="font-medium text-gray-700">{scheduleInfo.defaultInfo.schedule}</span>
            {schedule && schedule !== scheduleInfo.defaultInfo.schedule && (
              <span className="ml-1 text-amber-600">(overridden to <span className="font-medium">{schedule}</span>)</span>
            )}
          </span>
          <span className="text-gray-300">|</span>
          <span>Period: <span className="font-medium text-gray-700">{scheduleInfo.defaultInfo.period}</span></span>
          <span className="text-gray-300">|</span>
          <span>Runs at: <span className="font-medium text-gray-700">{scheduleInfo.defaultInfo.time}</span></span>
          <span className="text-gray-300">|</span>
          <span>Mode: <span className="font-medium text-gray-700">UPSERT (append)</span></span>
        </div>
      )}

      {/* Last sync info bar */}
      {lastJob && (
        <div
          className={`flex items-center gap-3 rounded-lg border px-4 py-2 text-sm cursor-pointer hover:opacity-80 ${
            lastJob.status === 'completed'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
          onClick={() => setShowJobHistory((v) => !v)}
        >
          <span className="font-medium">
            {lastJob.status === 'completed' ? '✓' : '✕'} Last sync: {lastJob.mode}
          </span>
          <span className="text-gray-500">·</span>
          <span>{formatRelative(lastJob.started_at)}</span>
          <span className="text-gray-500">·</span>
          <span>+{lastJob.rows_added ?? 0} rows</span>
          {lastJob.duration_ms && (
            <>
              <span className="text-gray-500">·</span>
              <span>{formatDuration(lastJob.duration_ms)}</span>
            </>
          )}
          {lastJob.total_chunks > 1 && (
            <>
              <span className="text-gray-500">·</span>
              <span>{lastJob.completed_chunks}/{lastJob.total_chunks} chunks</span>
            </>
          )}
          {lastJob.error_message && (
            <>
              <span className="text-gray-500">·</span>
              <span className="text-red-600">{lastJob.error_message}</span>
            </>
          )}
          <span className="ml-auto text-xs text-gray-400">{showJobHistory ? '▲' : '▼'} history</span>
        </div>
      )}

      {/* Job history panel (expandable) */}
      {showJobHistory && syncStatus?.recentJobs && syncStatus.recentJobs.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
            Sync History (last {syncStatus.recentJobs.length} jobs)
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500">
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium">Started</th>
                <th className="px-3 py-2 font-medium">Duration</th>
                <th className="px-3 py-2 font-medium">Rows</th>
                <th className="px-3 py-2 font-medium">Chunks</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {syncStatus.recentJobs.map((job) => (
                <tr key={job.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700'
                        : job.status === 'running' ? 'bg-blue-100 text-blue-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {job.status === 'running' && <SpinnerIcon />}
                      {job.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{job.mode}</td>
                  <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{new Date(job.started_at).toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-gray-600">{formatDuration(job.duration_ms)}</td>
                  <td className="px-3 py-1.5 font-medium">+{job.rows_added ?? 0}</td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {job.total_chunks > 1 ? (
                      <span>
                        <span className="text-green-600">{job.completed_chunks}</span>
                        {(job.failed_chunks ?? 0) > 0 && <span className="text-red-600">+{job.failed_chunks} err</span>}
                        <span className="text-gray-400">/{job.total_chunks}</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">1</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-red-500 max-w-xs truncate">
                    {job.error_message ? job.error_message.split('\n')[0] : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inline sync progress bar (always visible during sync) */}
      {syncing && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
          <div className="flex items-center gap-3 text-sm">
            <SpinnerIcon />
            <span className="font-medium text-blue-700">Syncing in progress...</span>
            {syncProgress ? (
              <>
                <span className="text-blue-600">+{syncProgress.rowsAdded} rows</span>
                <span className="text-gray-400">·</span>
                <span className="text-blue-600">{syncProgress.rowCountNow.toLocaleString()} total</span>
                <span className="text-gray-400">·</span>
                <span className="text-blue-600">{syncProgress.elapsedFormatted}</span>
                {syncProgress.total_chunks > 1 && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className="text-blue-600">chunk {syncProgress.completed_chunks}/{syncProgress.total_chunks}</span>
                  </>
                )}
              </>
            ) : (
              <span className="text-blue-600">Waiting for first results...</span>
            )}
          </div>
          {syncProgress && syncProgress.total_chunks > 1 && (
            <div className="mt-2 w-full bg-blue-200 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all"
                style={{ width: `${Math.max(5, (syncProgress.completed_chunks / syncProgress.total_chunks) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Sync Dialog */}
      {syncDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">Sync {name}</h3>

            {/* Accounts & Marketplaces */}
            {syncAccounts.length > 0 && (
              <div className="mb-4 rounded-lg bg-gray-50 p-3 space-y-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Syncing from</div>
                {syncAccounts.map((acc) => (
                  <div key={acc.id} className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{acc.name}</span>
                    <span className="text-gray-400">({acc.region})</span>
                    <span className="text-gray-300">—</span>
                    <div className="flex flex-wrap gap-1">
                      {acc.marketplaces.map((mp) => (
                        <span key={mp.id} className="rounded bg-white border border-gray-200 px-1.5 py-0.5 text-xs font-mono">
                          {mp.nickname}
                        </span>
                      ))}
                      {acc.marketplaces.length === 0 && (
                        <span className="text-xs text-gray-400 italic">no marketplaces configured</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3 mb-4">
              <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input type="radio" name="syncMode" checked={syncMode === 'default'} onChange={() => setSyncMode('default')} />
                <div>
                  <div className="text-sm font-medium">Default ({scheduleInfo?.defaultInfo?.period ?? 'last 30 days'})</div>
                  <div className="text-xs text-gray-500">Quick refresh of recent data</div>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input type="radio" name="syncMode" checked={syncMode === 'range'} onChange={() => setSyncMode('range')} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Date range</div>
                  <div className="text-xs text-gray-500 mb-2">Pull data for a specific period</div>
                  {syncMode === 'range' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={syncStart}
                        onChange={(e) => setSyncStart(e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="date"
                        value={syncEnd}
                        onChange={(e) => setSyncEnd(e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    </div>
                  )}
                </div>
              </label>

              <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input type="radio" name="syncMode" checked={syncMode === 'full'} onChange={() => setSyncMode('full')} />
                <div>
                  <div className="text-sm font-medium">Full history</div>
                  <div className="text-xs text-gray-500">Pull all available data (up to 2 years, recent months first)</div>
                </div>
              </label>
            </div>

            {/* Sync progress panel */}
            {syncing && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                  <SpinnerIcon /> Syncing in progress...
                </div>
                {syncProgress && (
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="rounded bg-white px-2 py-1.5 text-center">
                      <div className="text-lg font-semibold text-blue-700">{syncProgress.rowsAdded}</div>
                      <div className="text-gray-500">rows added</div>
                    </div>
                    <div className="rounded bg-white px-2 py-1.5 text-center">
                      <div className="text-lg font-semibold text-blue-700">{syncProgress.rowCountNow.toLocaleString()}</div>
                      <div className="text-gray-500">total rows</div>
                    </div>
                    <div className="rounded bg-white px-2 py-1.5 text-center">
                      <div className="text-lg font-semibold text-blue-700">{syncProgress.elapsedFormatted}</div>
                      <div className="text-gray-500">elapsed</div>
                    </div>
                  </div>
                )}
                {!syncProgress && (
                  <div className="text-xs text-blue-600">Waiting for first results...</div>
                )}
              </div>
            )}
            {!syncing && syncResult?.ok && (
              <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 mb-4 text-sm text-green-700">
                {syncResult.message}
              </div>
            )}
            {syncResult?.error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 mb-4 text-sm text-red-700">
                {syncResult.error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSyncDialog(false)}
                className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                {syncResult?.ok ? 'Close' : 'Cancel'}
              </button>
              {!syncResult?.ok && (
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center gap-1.5 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {syncing && <SpinnerIcon />}
                  {syncing ? 'Syncing...' : 'Start Sync'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab('data')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'data' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Data
        </button>
        <button
          onClick={() => setTab('schema')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'schema' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Schema
        </button>
      </div>

      {tab === 'schema' ? (
        <SchemaView columns={schema} />
      ) : (
        <>
          {/* Search + bulk actions */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-1 items-center gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search across all columns..."
                className="w-full max-w-md rounded border border-gray-300 px-3 py-1.5 text-sm"
              />
              <button
                onClick={handleSearch}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
              >
                Search
              </button>
              {search && (
                <button
                  onClick={() => { setSearch(''); setSearchInput(''); setPage(0); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear
                </button>
              )}
            </div>
            {selected.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete {selected.size} rows
              </button>
            )}
          </div>

          {/* Data table */}
          {rows && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={rows.rows.length > 0 && rows.rows.every((r) => selected.has(getRowId(r)))}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    {rows.columns.map((col) => (
                      <th
                        key={col}
                        onClick={() => handleSort(col)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 font-medium text-xs text-gray-600 whitespace-nowrap"
                      >
                        {col}
                        {sort?.col === col && (
                          <span className="ml-1">{sort.dir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    ))}
                    <th className="px-3 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.rows.map((row) => {
                    const rowid = (row.rowid ?? row.id ?? row.ID) as number | string;
                    return (
                      <tr key={rowid} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={selected.has(rowid)}
                            onChange={() => toggleSelect(rowid)}
                            className="rounded"
                          />
                        </td>
                        {rows.columns.map((col) => {
                          const isEditing = editCell?.rowid === rowid && editCell?.col === col;
                          const val = row[col];
                          const display = val === null ? 'NULL' : String(val);
                          return (
                            <td
                              key={col}
                              onClick={() => {
                                if (!isEditing) {
                                  setEditCell({ rowid, col, value: val === null ? '' : String(val) });
                                }
                              }}
                              className="px-3 py-1.5 max-w-xs truncate cursor-pointer"
                            >
                              {isEditing ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editCell.value}
                                    onChange={(e) => setEditCell({ ...editCell, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveCell();
                                      if (e.key === 'Escape') setEditCell(null);
                                    }}
                                    className="w-full rounded border border-blue-400 px-1.5 py-0.5 text-sm"
                                    autoFocus
                                  />
                                  <button
                                    onClick={handleSaveCell}
                                    disabled={saving}
                                    className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700"
                                  >
                                    {saving ? '...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => setEditCell(null)}
                                    className="text-xs text-gray-400 hover:text-gray-600"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <span className={`font-mono text-xs ${val === null ? 'text-gray-300 italic' : ''}`}>
                                  {display.length > 80 ? display.slice(0, 80) + '…' : display}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5">
                          <button
                            onClick={() => handleDelete(rowid)}
                            className="text-xs text-red-400 hover:text-red-600"
                            title="Delete row"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.rows.length === 0 && (
                    <tr>
                      <td colSpan={rows.columns.length + 2} className="px-4 py-8 text-center text-gray-400">
                        No rows found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows?.total ?? 0)} of {rows?.total.toLocaleString()}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-30"
                >
                  Prev
                </button>
                <span className="flex items-center px-3 text-gray-600">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-30"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Schema View ── */

function SchemaView({ columns }: { columns: ColumnInfo[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-600">
            <th className="px-4 py-2 font-medium">#</th>
            <th className="px-4 py-2 font-medium">Column</th>
            <th className="px-4 py-2 font-medium">Type</th>
            <th className="px-4 py-2 font-medium">Not Null</th>
            <th className="px-4 py-2 font-medium">Default</th>
            <th className="px-4 py-2 font-medium">PK</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col) => (
            <tr key={col.cid} className="border-t border-gray-100">
              <td className="px-4 py-2 text-gray-400">{col.cid}</td>
              <td className="px-4 py-2 font-mono font-medium">{col.name}</td>
              <td className="px-4 py-2 text-gray-600">{col.type || 'TEXT'}</td>
              <td className="px-4 py-2">{col.notnull ? '✓' : ''}</td>
              <td className="px-4 py-2 font-mono text-xs text-gray-500">{col.dflt_value ?? '—'}</td>
              <td className="px-4 py-2">{col.pk ? '🔑' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Helpers ── */

function SqlIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <SpinnerIcon size={6} />
    </div>
  );
}

function SpinnerIcon({ size = 4 }: { size?: number }) {
  return (
    <svg className={`h-${size} w-${size} animate-spin`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
